import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { fetchSettings, adminUpdateSettings } from '../../lib/adminApi';
import { clearSettingsCache } from '../../hooks/useSettings';
import { useToast } from '../../hooks/use-toast';

const input = 'w-full bg-white border border-[#d8d2c6] px-3 py-2.5 outline-none focus:border-[#17565f] transition-colors rounded';
const FIELDS = [
  { k: 'serviceTime', label: 'Service Time' },
  { k: 'address', label: 'Street Address' },
  { k: 'city', label: 'City / State / Zip' },
  { k: 'email', label: 'Contact Email' },
  { k: 'phone', label: 'Phone Number' },
  { k: 'poBox', label: 'PO Box' },
  { k: 'poCity', label: 'PO Box City / Zip' },
  { k: 'facebookUrl', label: 'Facebook Page URL' },
];

const SettingsManager = () => {
  const { toast } = useToast();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setForm(await fetchSettings());
    } catch (err) {
      console.error('Failed to load settings:', err);
      setForm({});
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminUpdateSettings(form);
      clearSettingsCache();
      toast({ title: 'Site info saved', description: 'Changes are now live on the website.' });
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Please try again.' });
    } finally { setSaving(false); }
  };

  if (!form) return <p className="text-[#5a6b71]">Loading…</p>;

  return (
    <div className="max-w-2xl">
      <h2 className="font-serif-display text-3xl text-[#0f2e38] mb-1">Site Information</h2>
      <p className="text-[#5a6b71] mb-8">Update service times and contact details shown across the website.</p>
      <form onSubmit={save} className="bg-white border border-[#e4ddd0] rounded-lg p-6 space-y-4">
        {FIELDS.map((f) => (
          <div key={f.k}>
            <label className="block text-sm text-[#3a4d54] mb-1">{f.label}</label>
            <input value={form[f.k] || ''} onChange={set(f.k)} className={input} />
          </div>
        ))}
        <div className="pt-2">
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-[#c9a24b] text-[#0b2027] hover:bg-[#d8b566] px-6 py-3 rounded font-700 transition-colors">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Changes
          </button>
        </div>
      </form>
    </div>
  );
};

export default SettingsManager;
